import { supabase } from '@supabaseClient/supabaseClient';

export type CustomFieldEntityType = 'order' | 'product';

export type CustomFieldType =
  | 'text'
  | 'number'
  | 'date'
  | 'boolean'
  | 'select'
  | 'textarea';

export interface ICustomFieldDefinition {
  id: string;
  organization_id: string;
  entity_type: CustomFieldEntityType;
  name: string;
  label: string;
  type: CustomFieldType;
  required: boolean;
  display_order: number;
  placeholder?: string | null;
  help_text?: string | null;
  default_value?: unknown;
  validation_rules?: Record<string, string>;
  field_options?: Array<{ value: string; label: string }>;
  active: boolean;
  created_at: string;
  updated_at: string;
}

export interface ICustomFieldValue {
  id: string;
  organization_id: string;
  entity_type: CustomFieldEntityType;
  entity_id: string;
  field_id: string;
  value_text?: string | null;
  value_number?: number | null;
  value_bool?: boolean | null;
  value_date?: string | null;
  value_json?: Record<string, unknown> | unknown[] | string | number | boolean | null;
  created_at: string;
  updated_at: string;
}

export class CustomFieldsClient {
  static async getDefinitionsByOrganizationId(
    organization_id: string,
    entity_type: CustomFieldEntityType
  ): Promise<ICustomFieldDefinition[]> {
    const { data, error } = await supabase
      .from('custom_field_definitions')
      .select('*')
      .eq('organization_id', organization_id)
      .eq('entity_type', entity_type)
      .eq('active', true)
      .order('display_order', { ascending: true });

    if (error) {
      throw error;
    }

    return data || [];
  }

  static async createDefinition(
    definition: Omit<ICustomFieldDefinition, 'id' | 'created_at' | 'updated_at'>
  ): Promise<ICustomFieldDefinition> {
    const { data, error } = await supabase
      .from('custom_field_definitions')
      .insert([definition])
      .select()
      .single();

    if (error) {
      throw error;
    }

    return data;
  }

  static async updateDefinition(
    definition: Omit<ICustomFieldDefinition, 'created_at' | 'updated_at'>
  ): Promise<ICustomFieldDefinition> {
    const { data, error } = await supabase
      .from('custom_field_definitions')
      .update(definition)
      .eq('id', definition.id)
      .select()
      .single();

    if (error) {
      throw error;
    }

    return data;
  }

  static async deleteDefinition(id: string): Promise<void> {
    const { error } = await supabase
      .from('custom_field_definitions')
      .delete()
      .eq('id', id);

    if (error) {
      throw error;
    }
  }

  static async deactivateDefinition(id: string): Promise<void> {
    const { error } = await supabase
      .from('custom_field_definitions')
      .update({ active: false })
      .eq('id', id);

    if (error) {
      throw error;
    }
  }

  static async getValuesByEntity(
    organization_id: string,
    entity_type: CustomFieldEntityType,
    entity_id: string
  ): Promise<ICustomFieldValue[]> {
    const { data, error } = await supabase
      .from('custom_field_values')
      .select('*')
      .eq('organization_id', organization_id)
      .eq('entity_type', entity_type)
      .eq('entity_id', entity_id);

    if (error) {
      throw error;
    }

    return data || [];
  }

  static async getValuesForEntities(
    organization_id: string,
    entity_type: CustomFieldEntityType,
    entity_ids: string[]
  ): Promise<ICustomFieldValue[]> {
    if (entity_ids.length === 0) return [];
    const MAX_BATCH = 500;
    const results: ICustomFieldValue[] = [];

    for (let i = 0; i < entity_ids.length; i += MAX_BATCH) {
      const batch = entity_ids.slice(i, i + MAX_BATCH);
      const { data, error } = await supabase.rpc('get_custom_field_values', {
        p_organization_id: organization_id,
        p_entity_type: entity_type,
        p_entity_ids: batch,
      });

      if (error) {
        throw error;
      }

      results.push(...(data || []));
    }

    return results;
  }

  static async upsertValues(
    values: Array<Omit<ICustomFieldValue, 'id' | 'created_at' | 'updated_at'>>
  ): Promise<ICustomFieldValue[]> {
    const { data, error } = await supabase
      .from('custom_field_values')
      .upsert(values, {
        onConflict: 'organization_id,entity_type,entity_id,field_id',
      })
      .select();

    if (error) {
      throw error;
    }

    return data || [];
  }

  static async deleteValuesForEntity(
    organization_id: string,
    entity_type: CustomFieldEntityType,
    entity_id: string
  ): Promise<void> {
    const { error } = await supabase
      .from('custom_field_values')
      .delete()
      .eq('organization_id', organization_id)
      .eq('entity_type', entity_type)
      .eq('entity_id', entity_id);

    if (error) {
      throw error;
    }
  }
}
